import type { ComponentType, ReactNode, RefObject } from 'react';

/**
 * Visual styles for the student announcement ("Comunicado") pop-up.
 *
 * Presentational only: the five cards in `./styles/` share
 * `AnnouncementCardProps` and know nothing about tRPC, routing or who is
 * logged in. The p2e gate (`apps/p2e/src/components/announcement-gate.tsx`)
 * owns the behaviour and picks a card; the admin renders the same cards in
 * its live preview (`AnnouncementPreview`), so what the founder sees while
 * writing is what the student gets.
 *
 * The list of names mirrors `ANNOUNCEMENT_STYLES` in `@org/model` (the
 * values the `announcements.style` column accepts).
 */
export const ANNOUNCEMENT_STYLE_NAMES = [
  'classic',
  'issue-cover',
  'wall-note',
  'school-letter',
  'extra-edition',
] as const;

export type AnnouncementStyleName = (typeof ANNOUNCEMENT_STYLE_NAMES)[number];

export function isAnnouncementStyleName(value: string | null | undefined): value is AnnouncementStyleName {
  return !!value && (ANNOUNCEMENT_STYLE_NAMES as readonly string[]).includes(value);
}

/**
 * What every styled card receives. The host owns behaviour (acknowledge,
 * media watch-tracking, escape/backdrop handling, focus); the card only
 * lays things out.
 */
export interface AnnouncementCardProps {
  title: string;
  body: string | null;
  /** Video or image element already wired for watch-tracking, or null. */
  media: ReactNode;
  /** When the announcement surfaced (startsAt, else createdAt). */
  date: Date;
  /** BCP-47 locale for date formatting ("pt-BR" / "en-US"). */
  locale: string;
  primaryLabel: string;
  primaryDisabled: boolean;
  primaryRef: RefObject<HTMLButtonElement>;
  onPrimary: () => void;
  skippable: boolean;
  skipLabel: string;
  onSkip: () => void;
  /** Shown under the actions while a mandatory video still has to finish. */
  gatedHint: string | null;
  /** Feminine copy is the default; male students get the `_male` variants. */
  genderContext?: 'male';
  reducedMotion: boolean;
}

export interface AnnouncementStyleDefinition {
  Card: ComponentType<AnnouncementCardProps>;
  /** On phones the overlay pins the card to the bottom edge (sheet). */
  sheetOnMobile?: boolean;
}

/**
 * Name of the CSS container the cards query for their phone layout. The p2e
 * overlay (fixed, inset 0 — so as wide as the viewport) and the admin preview
 * frame both declare it, which is what lets a 360px preview frame show the
 * phone layout inside a wide admin window.
 */
export const ANNOUNCEMENT_CONTAINER_NAME = 'announcement';

export const announcementContainerStyle: React.CSSProperties = {
  containerType: 'inline-size',
  containerName: ANNOUNCEMENT_CONTAINER_NAME,
};

/** Shared palette (same values as journey.tsx / mural.tsx / the zine theme). */
export const ANNOUNCEMENT_PALETTE = {
  maxRed: '#D41920',
  redDeep: '#B5131A',
  redShadow: '#8E1218',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  ink: '#1A1018',
  paper: '#FFF8F5',
  cream: '#FEF3EF',
  blush: '#FDF0ED',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightBorder: '#F0DAD5',
  dek: '#FCE3E0',
  mutedOnDark: '#C9AEB4',
  white: '#FFFFFF',
} as const;

export const ANNOUNCEMENT_FONTS = {
  cond: "'Oswald', 'Arial Narrow', sans-serif",
  serif: "'DM Serif Text', Georgia, serif",
  sans: "'Montserrat', system-ui, sans-serif",
} as const;

/** Oswald-caps button base shared by every style (mirrors the classic card). */
export const announcementButtonBase: React.CSSProperties = {
  minHeight: 44,
  padding: '0 24px',
  fontFamily: ANNOUNCEMENT_FONTS.cond,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  borderRadius: 2,
  border: '1px solid transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export function formatAnnouncementDate(date: Date, locale: string, style: 'long' | 'short' = 'long'): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: style === 'long' ? 'long' : 'short',
      ...(style === 'long' ? { year: 'numeric' } : {}),
    }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}
